import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/sales/auth/AuthContext";
import { AlertCircle, KeyRound, User as UserIcon, ArrowRight, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, isAuthenticated, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/sales";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (ready && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Vul gebruikersnaam en wachtwoord in.");
      return;
    }
    setSubmitting(true);
    try {
      await login(username.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Inloggen mislukt. Controleer je gegevens.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      className="min-h-screen w-full relative overflow-hidden bg-[#0B1521] text-[#F8FAFC] grid place-items-center px-6"
      data-testid="login-page"
    >
      {/* Ambient radial glow */}
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(28,58,90,0.7) 0%, transparent 55%), radial-gradient(circle at 75% 80%, rgba(192,192,192,0.08) 0%, transparent 50%)",
        }}
      />
      {/* Subtle silver hairline grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#C0C0C0 1px, transparent 1px), linear-gradient(90deg, #C0C0C0 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative w-full max-w-[440px] fade-up">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-10 justify-center">
          <img
            src="https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/2bbd7681a8a04908b46aa22122b6939c_Gemini_Generated_Image_32yxxe32yxxe32yx.png"
            alt="RiskaiΛ"
            data-testid="login-logo"
            style={{ height: "56px", width: "auto", mixBlendMode: "lighten" }}
            className="select-none"
          />
          <div className="text-left">
            <h1 className="font-serif-display text-2xl font-semibold tracking-tight leading-none">
              Riskai<span className="text-[#C0C0C0]">Λ</span>
            </h1>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#94A3B8] mt-1">
              Sales Control Center
            </p>
          </div>
        </div>

        {/* Card */}
        <div
          className="relative rounded-xl border border-[rgba(192,192,192,0.18)] bg-gradient-to-br from-[#142539] to-[#0B1521] p-8 md:p-10 shadow-2xl"
          style={{ boxShadow: "0 0 0 1px rgba(192,192,192,0.08), 0 30px 60px -20px rgba(0,0,0,0.6)" }}
        >
          {/* Top silver thread */}
          <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#C0C0C0] to-transparent opacity-60" />

          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#C0C0C0] border border-[rgba(192,192,192,0.3)] rounded-full px-3 py-1">
              <ShieldCheck size={11} /> Beveiligde toegang
            </span>
          </div>
          <h2 className="font-serif-display text-3xl font-medium text-[#F8FAFC] leading-tight">
            Welkom terug
          </h2>
          <p className="text-sm text-[#94A3B8] mt-1.5 mb-7 leading-relaxed">
            Log in met je RiskaiΛ-account om de pipeline en je trajecten te bekijken.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-2 font-semibold">
                Gebruikersnaam
              </label>
              <div className="relative">
                <UserIcon
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]"
                />
                <input
                  type="text"
                  autoComplete="username"
                  autoFocus
                  data-testid="login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="ben"
                  className="w-full bg-[#0B1521] border border-[rgba(192,192,192,0.2)] focus:border-[#C0C0C0] rounded-md pl-10 pr-3 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-2 font-semibold">
                Wachtwoord
              </label>
              <div className="relative">
                <KeyRound
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]"
                />
                <input
                  type="password"
                  autoComplete="current-password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#0B1521] border border-[rgba(192,192,192,0.2)] focus:border-[#C0C0C0] rounded-md pl-10 pr-3 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none transition-colors"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 px-3.5 py-2.5 rounded-md bg-[#7F1D1D]/30 border border-rose-500/40 text-rose-200 text-xs"
              >
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="group w-full inline-flex items-center justify-center gap-2 bg-[#C0C0C0] hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed text-[#0B1521] text-sm font-semibold rounded-md px-4 py-3 transition-all"
            >
              {submitting ? "Bezig met inloggen…" : "Inloggen"}
              {!submitting && (
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-[10px] uppercase tracking-[0.24em] text-[#475569] mt-6">
          © RiskaiΛ · Gouden Standaard · Editie 2026
        </p>
      </div>
    </main>
  );
}
